import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Button, IconButton } from "../../../../shared";
import { ChevronRight, FileDown, MessageCircle, MapPin } from "lucide-react";

import { usePastelColors } from "../../../../hooks";

export function TitleGFC(props: any) {
  const { Name, Description, Id, Color } = props.data;
  const pastel = usePastelColors(Id);
  const dotColor = Color || pastel;

  return (
    <Box
      py={1}
      pl={1.5}
      display="flex"
      alignItems="center"
      gap={2}
      sx={{ minWidth: 0, width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: dotColor,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Tooltip title={Name || ""} arrow placement="top" enterDelay={400}>
          <Typography
            sx={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {Name}
          </Typography>
        </Tooltip>
        <Tooltip title={Description || "No description"} arrow placement="top" enterDelay={400}>
          <Typography
            level="body-sm"
            color="neutral"
            sx={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {Description || "No description"}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function TaskSummaryGFC({ data }: any) {
  const { TaskSummary } = data;
  const completed = TaskSummary?.COMPLETED || 0;
  const total = TaskSummary?.TotalTasks || 1;
  const percent = Math.round((completed / total) * 100);

  return (
    <Box width={"100%"} pr={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={0.5}
      >
        <Typography level="body-sm">Progress</Typography>
        <Typography level="body-sm" color="neutral">
          {completed}/{total} Tasks
        </Typography>
      </Box>
      <LinearProgress
        variant="soft"
        determinate
        value={percent}
        color={
          percent === 100 ? "success" : percent > 0 ? "primary" : "neutral"
        }
        thickness={6}
        sx={{
          borderRadius: "sm",
        }}
      />
    </Box>
  );
}

export function SalesNdStatusGFC({ data }: any) {
  const { Status, SalesmanName = "Unassigned" } = data;

  let statusColor = "neutral";
  let displayStatus = Status;

  if (Status === "CANCELLED") {
    statusColor = "danger";
    displayStatus = "Cancelled";
  } else if (Status === "IN-PROGRESS") {
    statusColor = "primary";
    displayStatus = "In Progress";
  } else if (Status === "COMPLETED") {
    statusColor = "success";
    displayStatus = "Completed";
  } else if (Status === "NOT-STARTED") {
    statusColor = "neutral";
    displayStatus = "Not Started";
  }

  return (
    <Box display="flex" alignItems="center" gap={1.5}>
      <Chip
        variant="soft"
        color={statusColor as any}
        size="sm"
        sx={{
          borderRadius: "sm",
          minWidth: 90,
          textAlign: "center",
          justifyContent: "center",
          textTransform: "capitalize",
        }}
      >
        {displayStatus?.toLowerCase()}
      </Chip>
      <Tooltip title={SalesmanName} arrow placement="top">
        <Avatar
          size="sm"
          variant="outlined"
          alt={SalesmanName}
          sx={{
            width: 28,
            height: 28,
            borderRadius: "sm",
            border: "2px solid",
            borderColor: "background.surface",
          }}
        >
          {SalesmanName?.charAt(0)}
        </Avatar>
      </Tooltip>
    </Box>
  );
}

export function CustomerAddressGFC({ data }: any) {
  const {
    CustomerStreet,
    CustomerCity,
    CustomerState = "—",
    CustomerZipCode,
  } = data;

  const hasAddress = CustomerStreet || CustomerCity;

  return (
    <Box display="flex" alignItems="center" gap={1.5} flex={1}>
      <Box sx={{ color: "neutral.400", display: "flex", alignItems: "center" }}>
        <MapPin size={16} />
      </Box>
      <Box>
        {hasAddress ? (
          <>
            <Typography level="body-sm" sx={{ color: "text.primary" }}>
              {CustomerStreet}, {CustomerCity}
            </Typography>
            <Typography level="body-sm" color="neutral">
              {CustomerState} {CustomerZipCode}
            </Typography>
          </>
        ) : (
          <Typography level="body-sm" color="neutral" fontStyle="italic">
            No address provided
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ActionButtonsGFC({ data }: any) {
  const { NoteCount = 0 } = data;
  return (
    <Box
      display="flex"
      gap={0.5}
      pr={1}
      alignItems="center"
      justifyContent="flex-end"
      width="100%"
    >
      <Tooltip title="Notes" arrow>
        <Button
          startDecorator={<MessageCircle size={14} />}
          size="sm"
          variant="plain"
          color="neutral"
          sx={{
            borderRadius: "sm",
            "&:hover": { bgcolor: "background.level1" },
          }}
        >
          {NoteCount}
        </Button>
      </Tooltip>
      <Tooltip title="Download" arrow>
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          sx={{
            borderRadius: "sm",
            "&:hover": { bgcolor: "background.level1" },
          }}
        >
          <FileDown size={16} />
        </IconButton>
      </Tooltip>
      <IconButton
        variant="plain"
        color="neutral"
        size="sm"
        sx={{
          borderRadius: "sm",
          ml: 0.5,
          "&:hover": {
            bgcolor: "background.level1",
            transform: "translateX(3px)",
          },
          transition: "all 0.2s",
        }}
      >
        <ChevronRight size={18} />
      </IconButton>
    </Box>
  );
}
